const useDynamicListBlock = (blockElement: HTMLElement): void => {
  const pagesToShow: number = parseInt((blockElement.firstElementChild as HTMLDivElement).dataset.pagesToShow!);
  const pages = Array.from(blockElement.querySelectorAll('.dynamic-list-block__page-card')) as HTMLLIElement[];
  const filterButtons = blockElement.querySelectorAll('.dynamic-list-block__filter-button') as NodeListOf<HTMLButtonElement>;
  const showMoreContainer: HTMLDivElement | null = blockElement.querySelector('.dynamic-list-block__show-more');
  const showMoreButton: HTMLButtonElement | null = blockElement.querySelector('.dynamic-list-block__show-more-button');
  const showMoreTextElement: HTMLSpanElement | null = showMoreButton?.querySelector('.dynamic-list-block__show-more-text') ?? null;
  const showMoreTemplate: string = (showMoreTextElement?.textContent ?? '').trim();
  const showMoreLabel: string = (showMoreButton?.dataset.showMoreLabel
    ?? showMoreButton?.textContent
    ?? '').trim();
  
  /* Show and hide pages */
  
  const buildShowMoreText = (count: number): string => {
    if (showMoreTemplate.length > 0 && /\d+/.test(showMoreTemplate)) {
      return showMoreTemplate.replace(/\d+/, count.toString());
    }

    const lastSpaceIndex = showMoreLabel.lastIndexOf(' ');
    if (lastSpaceIndex >= 0) {
      return `${showMoreLabel.slice(0, lastSpaceIndex)} ${count} ${showMoreLabel.slice(lastSpaceIndex + 1)}`;
    }

    return `${showMoreLabel} ${count}`;
  };

  const updateShowMoreButton = (pagesToUpdate: HTMLLIElement[]): void => {
    if (!showMoreContainer || !showMoreButton) {
      return;
    }

    const hiddenPages = pagesToUpdate.filter((page) => page.hidden);
    if (hiddenPages.length === 0) {
      showMoreContainer.hidden = true;
      return;
    }

    const pagesToShowCount = Math.min(pagesToShow, hiddenPages.length);
    if (showMoreTextElement) {
      showMoreTextElement.textContent = buildShowMoreText(pagesToShowCount);
    }

    showMoreContainer.hidden = false;
  };

  // Show all pages and "Show more" button if needed
  const showAllPages = (): void => {
    pages.forEach((page, index) => {
      if (index < pagesToShow && page.hidden) {
        page.hidden = false;
      }

      if (page.classList.contains('dynamic-list-block__page-card--filtered')) {
        page.classList.remove('dynamic-list-block__page-card--filtered');
      }
    });

    updateShowMoreButton(pages);
  }
  
  // Hide all pages and remove "Show more" button
  const hideAllPages = (): void => {
    pages.forEach((page) => {
      if (!page.hidden) {
        page.hidden = true;
      }
      
      if (page.classList.contains('dynamic-list-block__page-card--filtered')) {
        page.classList.remove('dynamic-list-block__page-card--filtered');
      }
    });

    // Hide "Show more" button
    if (showMoreContainer && !showMoreContainer.hidden) {
      showMoreContainer.hidden = true;
    }
  };
  
  const showMorePages = (pages: HTMLLIElement[], maxIndexToShow: number, firstShownPageIndex: number): void => {
    pages.forEach((page, index) => {
      if (page.hidden && index <= maxIndexToShow) {
        page.hidden = false;

        // Fade in animation
        page.classList.add('dynamic-list-block__page-card--fade-in');
        
        setTimeout(() => {
          page.classList.remove('dynamic-list-block__page-card--fade-in');
        }, 100);
      }
    });
    
    updateShowMoreButton(pages);
    
    // Set focus to the first shown page
    let firstShownPage: HTMLLIElement | null = pages[firstShownPageIndex];
    if (firstShownPage) {
      let firstShownPageLink = firstShownPage.querySelector('a') as HTMLAnchorElement;
      firstShownPageLink.focus();
    }
  }

  const showPagesWithSelectedCategories = (selectedCategoryIds: number[]): HTMLLIElement[] => {
    const pagesWithCategories = pages.filter((page) => page.firstElementChild!.hasAttribute('data-category-ids'));
    const pagesWithCategoriesToShow: HTMLLIElement[] = [];

    pagesWithCategories.forEach((page) => {
      const pageArticle = page.firstElementChild as HTMLElement;
      const pageCategoryIds: number[] = JSON.parse(pageArticle.dataset.categoryIds!);

      // Show pages that match the selected categories
      if (pageCategoryIds.some((id) => selectedCategoryIds.includes(id))) {
        pagesWithCategoriesToShow.push(page);
      }

      // Hide all selected pages initially
      page.hidden = true;

      // Show as many pages as pagesToShow
      pagesWithCategoriesToShow.forEach((page, index) => {
        page.classList.add('dynamic-list-block__page-card--filtered');

        if (index < pagesToShow) {
          page.hidden = false;
        }
      });
    });

    updateShowMoreButton(pagesWithCategoriesToShow);
    return pagesWithCategoriesToShow;
  }

  /* Handle events */
  
  const handleShowMoreEvent = (): void => {
    const visiblePages = pages.filter((page) => !page.hidden);
    const lastVisiblePageIndex = visiblePages.length - 1;
    const maxIndexToShow = lastVisiblePageIndex + pagesToShow;
    const filteredPages = pages.filter((page) => page.classList.contains('dynamic-list-block__page-card--filtered'));
    
    if (filteredPages.length > 0) {
      showMorePages(filteredPages, maxIndexToShow, lastVisiblePageIndex + 1);
    } else {
      showMorePages(pages, maxIndexToShow, lastVisiblePageIndex + 1);
    }
  };

  const handleFilterEvent = (event: MouseEvent): void => {
    const filterButton = event.target as HTMLButtonElement;
    let selectedCategoryIds: number[] = [];
    
    filterButton.classList.toggle('button--selected');
    
    hideAllPages();

    // Store selected category id:s
    const selectedFilterButtons = blockElement.querySelectorAll('.button--filter.button--selected') as NodeListOf<HTMLButtonElement>;
    selectedFilterButtons.forEach((button) => {
      selectedCategoryIds.push(parseInt(button.parentElement!.dataset.categoryId!));
    });
    
    // Show pages based on selected category id:s
    if (selectedCategoryIds.length > 0) {
      showPagesWithSelectedCategories(selectedCategoryIds);
    } else {
      showAllPages();
    }
  }; 
  
  /* Initializing and adding event listeners */
  const initialize = (): void => {
      if (showMoreButton) {
        showMoreButton.addEventListener('click', handleShowMoreEvent);
      }
        
      if (filterButtons.length > 0) {
        filterButtons.forEach((button) => {
          button.addEventListener('click', handleFilterEvent);
        });
      }
  };

  initialize();
};

document.addEventListener('DOMContentLoaded', () => {
  const dynamicListBlockElements = document.querySelectorAll('.dynamic-list-block') as NodeListOf<HTMLElement>;

  if (dynamicListBlockElements.length > 0) {
    dynamicListBlockElements.forEach((blockElement) => {
      useDynamicListBlock(blockElement);
    })
  }
});
